import SEO from '@/components/SEO';
import BrandMarquee from '@/components/BrandMarquee';
import { site } from '@/data/content';
import {
  Globe,
  ArrowUpRight,
  Check,
  Sparkles,
  Target,
  Clock,
  Users,
  Search,
  BookOpen,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  Zap,
} from 'lucide-react';
import { useState } from 'react';

const features = [
  { icon: Globe, title: 'Branded help center', text: 'Publish articles on your own custom domain with your logo and colors.' },
  { icon: Sparkles, title: 'AI answers', text: 'Zyra AI answers questions using your knowledge base and past conversations.' },
  { icon: Search, title: 'Instant search', text: 'Customers find the right article in seconds with typo-tolerant search.' },
  { icon: MessageSquare, title: 'Seamless handoff', text: 'Start a chat or ticket from any article with full context.' },
];

const benefits = [
  { icon: Target, title: '40%+ ticket deflection', description: 'Common questions are answered automatically, reducing support volume.' },
  { icon: Clock, title: '24/7 availability', description: 'Customers get answers day or night, even when your team is offline.' },
  { icon: Users, title: '+22% CSAT', description: 'Customers prefer self-service when it works well and is easy to use.' },
  { icon: Zap, title: 'Fast deployment', description: 'Launch your help center in minutes with our templates and tools.' },
];

const useCases = [
  { title: 'FAQ deflection', text: 'Answer delivery, pricing, and account questions before they become tickets.' },
  { title: 'Onboarding guides', text: 'Help new customers set up, configure, and get value faster.' },
  { title: 'Internal wikis', text: 'Give agents and staff a searchable source of truth.' },
  { title: 'Product documentation', text: 'Publish setup guides, troubleshooting, and release notes.' },
];

const faqs: Array<{ question: string; answer: string }> = [
  {
    question: 'Do I need a separate domain for the help center?',
    answer: 'No. You can use a subdomain like help.yourcompany.com or host it on your main domain. We provide SSL certificates and custom domain setup.',
  },
  {
    question: 'How does Zyra AI work with the help center?',
    answer: 'Zyra AI reads your help center articles and conversation history to answer customer questions instantly. If it cannot answer, it escalates to a human with full context.',
  },
  {
    question: 'Can I make articles private?',
    answer: 'Yes. You can publish public articles for customers and private wikis for internal teams. Each article can be set to public, private, or password-protected.',
  },
  {
    question: 'What happens when customers cannot find an answer?',
    answer: 'Customers can start a chat or submit a ticket directly from any article. The article they were reading is attached to the conversation so agents have full context.',
  },
];

function FAQAccordion({ faqs }: { faqs: Array<{ question: string; answer: string }> }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="space-y-3">
      {faqs.map((faq, index) => (
        <div
          key={index}
          className="rounded-2xl border border-slate-200 bg-white shadow-sm"
        >
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="flex w-full items-center justify-between p-5 text-left"
            aria-expanded={openIndex === index}
          >
            <span className="pr-4 font-semibold text-slate-900">{faq.question}</span>
            {openIndex === index ? (
              <ChevronUp className="h-5 w-5 shrink-0 text-slate-500" />
            ) : (
              <ChevronDown className="h-5 w-5 shrink-0 text-slate-500" />
            )}
          </button>
          {openIndex === index && (
            <div className="px-5 pb-5">
              <p className="text-slate-600">{faq.answer}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function SelfServicePage() {
  return (
    <>
      <SEO
        title="Self Service - 24/7 Customer Help Center | ZuriHub"
        description="Help customers find answers 24/7 with a branded knowledge base, AI chat, and smart search. Reduce ticket volume and cut first-response time with ZuriHub Self Service."
        path="/self-service"
      />
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-32 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-4 py-1.5 text-sm font-semibold text-brand-300 backdrop-blur">
                <Globe className="h-4 w-4" /> Pro & Enterprise
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                Let customers solve their own <span className="gradient-text">problems, day or night</span>
              </h1>
              <p className="mt-6 text-lg text-slate-300">
                A branded help center with AI answers, instant search, and seamless escalation. Reduce ticket volume and improve customer satisfaction.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 transition hover:bg-slate-100 shadow-xl shadow-white/10"
                >
                  Start Free Trial <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href="/book-a-demo"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-8 py-4 text-base font-semibold text-white backdrop-blur transition hover:border-brand-500 hover:bg-slate-800"
                >
                  Book a demo <Globe className="h-4 w-4" />
                </a>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-600/20 to-brand-400/10 blur-3xl" />
              <div className="relative">
                <img
                  src="https://media.getzurihub.com/Jayrah%20Assets/Knowledgebase.png"
                  alt="ZuriHub Self Service help center showing branded knowledge base and AI chat"
                  className="rounded-3xl shadow-2xl shadow-brand-900/30"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandMarquee compact />

      <section className="py-24" aria-labelledby="features-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="features-heading" className="section-title">Powerful self-service features</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need for a modern help center.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f) => (
              <div key={f.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <f.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="benefits-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="benefits-heading" className="section-title">Why teams choose self-service</h2>
            <p className="section-subtitle mx-auto mt-4">Benefits that transform customer support.</p>
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <benefit.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{benefit.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="usecases-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 id="usecases-heading" className="text-center section-title">Use cases</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2">
            {useCases.map((u) => (
              <div key={u.title} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                <h3 className="font-bold text-slate-900">{u.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BrandMarquee />

      <section className="bg-slate-900 py-24 text-white" aria-labelledby="how-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <h2 id="how-heading" className="text-3xl font-extrabold tracking-tight sm:text-4xl">How it works</h2>
              <p className="mt-4 text-slate-300">
                Create a branded help center, train AI on your content, and let customers find answers 24/7 without waiting for your team.
              </p>
              <div className="mt-8 space-y-4">
                {[
                  {
                    icon: BookOpen,
                    title: 'Create categories',
                    description: 'Organize help content by product, topic, or audience.',
                  },
                  {
                    icon: Zap,
                    title: 'Write and publish',
                    description: 'Draft SEO-friendly articles and publish them publicly or privately.',
                  },
                  {
                    icon: Sparkles,
                    title: 'Train Zyra AI',
                    description: 'Point Zyra at your help center so it can answer questions instantly.',
                  },
                  {
                    icon: Target,
                    title: 'Measure and improve',
                    description: 'See what customers search for and fill the content gaps.',
                  },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-100 text-brand-600">
                      <item.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-bold">{item.title}</h3>
                      <p className="mt-1 text-sm text-slate-400">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-3xl border border-slate-700 bg-slate-800/50 p-8">
              <h3 className="font-bold">Self-service features</h3>
              <ul className="mt-4 space-y-3 text-slate-300">
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Branded help center</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Custom domain support</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> AI-powered answers</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Instant search</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Chat and ticket escalation</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Public and private articles</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 px-6 py-16 text-center text-white sm:px-12">
          <h2 className="text-3xl font-extrabold sm:text-4xl">Launch your help center today</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-brand-100">
            Give customers 24/7 self-service with a branded knowledge base and AI answers.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-bold text-brand-700 transition hover:bg-slate-100"
          >
            Create your knowledge base <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="faq-heading" className="section-title">Frequently asked questions</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need to know about self-service</p>
          </div>
          <FAQAccordion faqs={faqs} />
        </div>
      </section>
    </>
  );
}
